'use client'

/**
 * HeaderTopPick — the global "Top Pick" chip in the top nav, sitting beside HeaderIndices. Shows the
 * one site-wide top play (the confluence-ranked pick that also drives HeroPick on /stock-market-today)
 * as a compact amber chip: TOP PICK · $SYMBOL · +X% · N boards, linking to that stock's hub.
 *
 * Mirrors HeaderIndices exactly: polls a 30s-cached endpoint (/api/market/top-pick) every 45s while the
 * tab is visible, and rides sharedFetch so the desktop + mobile copies collapse onto one request.
 *
 * ⚠ WORDING IS DELIBERATELY TENSE-NEUTRAL ("Top Pick", never "now/live/today") so the chip does not
 * assert a live market off-hours. The day % is a factual day-over-day number, safe at any hour.
 */
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Target } from 'lucide-react'
import { sharedFetch } from '@/lib/sharedFetch'

type Pick = { symbol: string; name: string | null; price: number | null; dayPct: number | null; boards: number; bias: string }

export default function HeaderTopPick({ variant = 'inline' }: { variant?: 'inline' | 'ribbon' } = {}) {
  const [pick, setPick] = useState<Pick | null>(null)

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const j = await sharedFetch<{ pick?: Pick | null }>('/api/market/top-pick', 30_000)
        if (alive && j.pick?.symbol) setPick(j.pick)
      } catch { /* keep last */ }
    }
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, 45_000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  if (!pick) return null
  const up = (pick.dayPct ?? 0) >= 0
  const pct = pick.dayPct == null ? '' : `${up ? '+' : ''}${pick.dayPct.toFixed(1)}%`
  const href = `/stocks/${encodeURIComponent(pick.symbol)}`

  if (variant === 'ribbon') {
    return (
      <Link href={href} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 transition-opacity hover:opacity-80">
        <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-300"><Target className="h-3 w-3" />Top Pick</span>
        <span className="text-[11px] font-extrabold text-foreground">${pick.symbol}</span>
        {pct && <span className={`text-[11px] font-bold tabular-nums ${up ? 'text-emerald-400' : 'text-rose-400'}`}>{pct}</span>}
      </Link>
    )
  }

  return (
    <Link
      href={href}
      title={`Top Pick: $${pick.symbol}${pick.name ? ` (${pick.name})` : ''} - on ${pick.boards} live board${pick.boards === 1 ? '' : 's'}${pct ? `, ${pct} today` : ''}`}
      className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] dark:bg-transparent dark:bg-gradient-to-br dark:from-amber-500/[0.14] dark:to-zinc-900/50 px-2.5 py-1.5 transition-opacity hover:opacity-80"
    >
      <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-300"><Target className="h-3 w-3" />Top Pick</span>
      <span className="text-[13px] font-extrabold text-foreground">${pick.symbol}</span>
      {pct && <span className={`text-[11px] font-bold tabular-nums ${up ? 'text-emerald-400' : 'text-rose-400'}`}>{pct}</span>}
      {pick.boards >= 2 && (
        <span className="hidden items-center rounded bg-amber-500/15 px-1 text-[9px] font-bold text-amber-200 ring-1 ring-amber-500/30 2xl:inline-flex">{pick.boards} boards</span>
      )}
    </Link>
  )
}
